/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * footerHtml — serialises page_config.footer to a standalone HTML string.
 *
 * WHY THIS EXISTS: a `render_mode: 'fullpage'` HTML/CSS section is a 100vh
 * iframe that scrolls internally. A footer rendered by React in the parent
 * document therefore sits *below* that scrollport, so the visitor gets two
 * scrollbars — one inside the pasted site, one for the footer. Injecting the
 * footer into the iframe's own document instead puts it after the last section
 * in a single continuous scroll, which is how a real site behaves.
 *
 * The iframe is sandboxed without allow-same-origin, so the parent cannot
 * append DOM to it — the markup has to be baked into the srcdoc as a string.
 * That means this module mirrors the rendering in
 * components/dynamic-page/DynamicFooter.js and ArceeDefaultFooter.js.
 * KEEP THE THREE IN SYNC when footer config keys or layout change.
 */

const SOCIAL_ICON = {
  twitter:   'bi-twitter-x',
  facebook:  'bi-facebook',
  instagram: 'bi-instagram',
  linkedin:  'bi-linkedin',
  youtube:   'bi-youtube',
  github:    'bi-github',
  tiktok:    'bi-tiktok',
  discord:   'bi-discord',
  whatsapp:  'bi-whatsapp',
  email:     'bi-envelope-fill',
  phone:     'bi-telephone-fill',
  website:   'bi-globe2',
};

const SHADOW = {
  none: 'none',
  sm:   '0 -2px 8px rgba(0,0,0,0.12)',
  md:   '0 -4px 20px rgba(0,0,0,0.18)',
  lg:   '0 -8px 36px rgba(0,0,0,0.24)',
};

const BS_COL = {
  1: 'col-12',
  2: 'col-12 col-sm-6',
  3: 'col-12 col-sm-6 col-md-4',
  4: 'col-12 col-sm-6 col-md-3',
};

// Author-supplied strings land in an iframe that runs scripts — escape them.
function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function flexJustify(alignment) {
  return { left: 'flex-start', center: 'center', right: 'flex-end' }[alignment] || 'flex-start';
}

function isDarkBg(bgColor) {
  if (!bgColor || !/^#[0-9a-f]{6}$/i.test(bgColor)) return false;
  const r = parseInt(bgColor.slice(1, 3), 16);
  const g = parseInt(bgColor.slice(3, 5), 16);
  const b = parseInt(bgColor.slice(5, 7), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 < 128;
}

// ─── Default (Arcee-branded) footer ───────────────────────────────────────────

function velLogoSvg(primary, secondary, size = 24) {
  return `<svg width="${size}" height="${size}" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" fill="none" style="filter:drop-shadow(0 4px 6px rgba(0,0,0,0.3))">
  <defs>
    <linearGradient id="arceeFooterLeaf" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${esc(primary)}" />
      <stop offset="60%" stop-color="${esc(secondary)}" />
      <stop offset="100%" stop-color="${esc(secondary)}" stop-opacity="0.6" />
    </linearGradient>
    <linearGradient id="arceeFooterHandle" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#2a2a2a" /><stop offset="100%" stop-color="#000000" />
    </linearGradient>
    <linearGradient id="arceeFooterRing" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#525252" /><stop offset="100%" stop-color="#171717" />
    </linearGradient>
  </defs>
  <path d="M128 2 C 180 50 215 140 170 200 C 160 212 150 220 148 224 L 108 224 C 106 220 96 212 86 200 C 41 140 76 50 128 2 Z" fill="url(#arceeFooterLeaf)" />
  <path d="M128 15 L128 215" stroke="#f3e8ff" stroke-width="2" stroke-linecap="round" opacity="0.7" />
  <ellipse cx="128" cy="222" rx="14" ry="4" fill="url(#arceeFooterRing)" />
  <rect x="116" y="222" width="24" height="32" rx="1" fill="url(#arceeFooterHandle)" />
</svg>`;
}

function rudraLogoSvg(color) {
  return `<svg width="140" height="32" viewBox="0 0 280 54" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="2" y="2" width="60" height="14" rx="5" fill="${esc(color)}" fill-opacity="0.8" />
  <rect x="2" y="20" width="60" height="14" rx="5" fill="${esc(color)}" fill-opacity="0.8" />
  <rect x="2" y="38" width="60" height="14" rx="5" fill="${esc(color)}" fill-opacity="0.8" />
  <circle cx="32" cy="27" r="6" fill="#ff811a" />
  <text x="75" y="30" fill="${esc(color)}" fill-opacity="0.9" font-family="Arial, sans-serif" font-weight="100" font-size="37">RUDRA</text>
  <text x="75" y="52" fill="${esc(color)}" fill-opacity="0.9" font-family="Arial, sans-serif" font-weight="100" font-size="24">CORESOFT</text>
</svg>`;
}

function buildDefaultFooterHtml(theme, bgColor = '#fafafa') {
  const dark        = isDarkBg(bgColor);
  const primary     = theme?.primary   || '#4e2e80';
  const secondary   = theme?.secondary || '#ff7a59';
  const accentColor = dark ? '#e2e8f0' : primary;
  const mutedColor  = dark ? '#94a3b8' : '#888';
  const dotColor    = dark ? accentColor : secondary;
  const borderColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  return `<footer style="background:${esc(bgColor)};font-size:0.9rem">
  <div class="container">
    <div style="border-top:1px solid ${borderColor};padding:16px 0;display:flex;flex-direction:column;align-items:center;gap:8px">
      <div style="display:flex;align-items:center;gap:6px;font-weight:500;color:${esc(mutedColor)};font-size:0.85rem">
        Powered by
        <a href="https://arcee.io" target="_blank" rel="noreferrer" style="text-decoration:none;display:flex;align-items:center;gap:5px">
          ${velLogoSvg(primary, secondary)}
          <span style="font-weight:800;font-size:1rem">
            <span style="color:${esc(accentColor)}">arcee</span><span style="color:${esc(dotColor)}">.io</span>
          </span>
        </a>
      </div>
      <div style="display:flex;flex-direction:column;align-items:center;gap:4px">
        <span style="font-size:0.75rem;color:${esc(mutedColor)}">Developed by</span>
        <a href="https://rudracoresoft.com" target="_blank" rel="noreferrer" style="display:block">
          ${rudraLogoSvg(accentColor)}
        </a>
      </div>
      <div style="font-size:0.7rem;color:${esc(mutedColor)};text-align:center">
        © ${new Date().getFullYear()} Rudra Coresoft Pvt Ltd. Bangalore, India. All Rights Reserved.
      </div>
    </div>
  </div>
</footer>`;
}

// ─── Custom (author-designed) footer ──────────────────────────────────────────

function buildCustomFooterHtml(cfg, theme) {
  const {
    layout                = 'columns',
    content_alignment     = 'left',
    columns               = [],
    brand_name            = '',
    brand_logo_url        = '',
    tagline               = '',
    social_links          = [],
    social_icon_size      = 18,
    bottom_bar_enabled    = true,
    copyright_text        = '',
    bottom_bar_links      = [],
    bottom_bar_alignment  = 'center',
    bg_color              = '#1e293b',
    text_color            = '#94a3b8',
    title_color           = '#ffffff',
    link_color            = '',
    bottom_bar_bg         = '',
    bottom_bar_text_color = '',
    font_size             = 14,
    title_font_size       = 13,
    font_weight           = '400',
    title_font_weight     = '700',
    padding_top           = 48,
    padding_bottom        = 32,
    column_gap            = 32,
    row_gap               = 10,
    min_height            = 0,
    border_top_enabled    = true,
    border_color          = 'rgba(255,255,255,0.1)',
    border_width          = 1,
    box_shadow            = 'none',
  } = cfg;

  const linkCol   = link_color || theme?.primary || '#cbd5e1';
  const colCount  = Math.min(Math.max(columns.length, 1), 4);
  const hasCols   = layout === 'columns' && columns.length > 0;
  const hasBrand  = !hasCols && (brand_name || brand_logo_url || tagline);
  const hasSocial = social_links.length > 0;
  const hasMain   = hasCols || hasBrand || hasSocial;
  const hasBottom = bottom_bar_enabled && (copyright_text || bottom_bar_links.length > 0);

  const colsHtml = !hasCols ? '' : `<div class="row" style="--bs-gutter-x:${column_gap}px;row-gap:${column_gap * 0.75}px">
${columns.map(col => `<div class="${BS_COL[colCount] || 'col-12'} arcee-df-col">
  ${col.title_visible !== false && col.title
    ? `<div style="color:${esc(title_color)};font-size:${title_font_size}px;font-weight:${esc(title_font_weight)};margin-bottom:14px;text-transform:uppercase;letter-spacing:0.07em;text-align:${esc(content_alignment)}">${esc(col.title)}</div>`
    : ''}
  <ul style="list-style:none;padding:0;margin:0;text-align:${esc(content_alignment)}">
${(col.items || []).map(item => `    <li style="margin-bottom:${row_gap + 2}px">${
      item.type === 'link'
        ? `<a href="${esc(item.url || '#')}"${item.open_new_tab ? ' target="_blank" rel="noopener noreferrer"' : ''} style="color:${esc(linkCol)};text-decoration:none;display:inline-flex;align-items:center;gap:6px">${
            item.icon ? `<i class="bi ${esc(item.icon)}" style="font-size:${font_size}px"></i>` : ''
          }${esc(item.label)}</a>`
        : `<span style="color:${esc(text_color)};line-height:1.6">${esc(item.text || item.label)}</span>`
    }</li>`).join('\n')}
  </ul>
</div>`).join('\n')}
</div>`;

  const brandHtml = !hasBrand ? '' : `<div style="text-align:center;margin-bottom:${hasSocial ? 28 : 0}px">
  ${brand_logo_url ? `<img src="${esc(brand_logo_url)}" alt="${esc(brand_name || 'Logo')}" style="max-height:56px;max-width:200px;object-fit:contain;margin-bottom:14px" onerror="this.style.display='none'" />` : ''}
  ${brand_name ? `<div style="color:${esc(title_color)};font-size:${title_font_size + 4}px;font-weight:700;margin-bottom:6px">${esc(brand_name)}</div>` : ''}
  ${tagline ? `<div style="opacity:0.72;max-width:520px;margin:0 auto;line-height:1.6">${esc(tagline)}</div>` : ''}
</div>`;

  const socialHtml = !hasSocial ? '' : `<div style="display:flex;flex-wrap:wrap;gap:10px;justify-content:${flexJustify(content_alignment)};margin-top:${(hasCols || hasBrand) ? 28 : 0}px">
${social_links.map(s => `  <a href="${esc(s.url || '#')}" target="_blank" rel="noopener noreferrer" title="${esc(s.label || s.platform)}" style="color:${esc(linkCol)};display:inline-flex;align-items:center;justify-content:center;width:${social_icon_size + 14}px;height:${social_icon_size + 14}px;border-radius:50%;border:1.5px solid ${esc(linkCol)}55;text-decoration:none;flex-shrink:0">
    <i class="bi ${esc(SOCIAL_ICON[s.platform] || 'bi-link-45deg')}" style="font-size:${social_icon_size}px"></i>
  </a>`).join('\n')}
</div>`;

  const mainHtml = !hasMain ? '' : `<div style="padding:${padding_top}px 0 ${padding_bottom}px">
  <div class="container">
${colsHtml}
${brandHtml}
${socialHtml}
  </div>
</div>`;

  const bottomHtml = !hasBottom ? '' : `<div style="background:${esc(bottom_bar_bg || 'rgba(0,0,0,0.18)')};border-top:1px solid ${esc(border_color)};padding:12px 0">
  <div class="container" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 20px;justify-content:${flexJustify(bottom_bar_alignment)}">
    ${copyright_text ? `<span style="font-size:${Math.max(font_size - 2, 11)}px;color:${esc(bottom_bar_text_color || text_color)};opacity:0.7">${esc(copyright_text)}</span>` : ''}
${bottom_bar_links.map(link => `    <a href="${esc(link.url || '#')}" style="color:${esc(linkCol)};text-decoration:none;font-size:${Math.max(font_size - 2, 11)}px;opacity:0.78">${esc(link.label)}</a>`).join('\n')}
  </div>
</div>`;

  return `<style>
@media (max-width: 575px) {
  .arcee-df-col { text-align: ${content_alignment} !important; }
  .arcee-df-col ul, .arcee-df-col a { justify-content: ${flexJustify(content_alignment)}; }
}
</style>
<footer style="background:${esc(bg_color)};color:${esc(text_color)};font-size:${font_size}px;font-weight:${esc(font_weight)};${min_height > 0 ? `min-height:${min_height}px;` : ''}${border_top_enabled ? `border-top:${border_width}px solid ${esc(border_color)};` : ''}box-shadow:${SHADOW[box_shadow] || 'none'}">
${mainHtml}
${bottomHtml}
</footer>`;
}

/**
 * Serialise a footer config to HTML for embedding inside a full-page iframe.
 * Returns '' when there is no footer to render.
 *
 * @param {object|null} footerCfg  page_config.footer
 * @param {object}      theme      active theme (primary/secondary)
 * @param {string}      mode       'default' | 'custom' (already resolved)
 */
export function buildFooterHtml(footerCfg, theme, mode) {
  if (!footerCfg?.enabled) return '';
  if (mode === 'default') return buildDefaultFooterHtml(theme);
  return buildCustomFooterHtml(footerCfg, theme);
}

export default buildFooterHtml;
